import NativeAd from "../ads/Native";
import { useState, useEffect } from "react";

import styles from "./ArticleRelatedContentList.module.scss";
import {
  RelatedContentAuthorFragment,
  RelatedContentRiverFragment,
} from "types/api";
import { gql } from "utils/urql";
import Authors from "components/Authors";
import Image from "components/Image";
import useMedia from "utils/useMedia";
import { useActiveTestGroup } from "utils/abTesting";
import cx from "classnames";

const AuthorWrapper = ({
  articleIndex,
  displayName,
  url,
  heading,
}: RelatedContentAuthorFragment & {
  articleIndex: number;
  heading: string;
}) => {
  return (
    <a
      className={styles.itemLink}
      href={url}
      data-action={`click link - ${heading.toLowerCase()} - author ${
        articleIndex + 1
      }`}
      data-label={url}
    >
      <span dangerouslySetInnerHTML={{ __html: displayName }} />
    </a>
  );
};

const RecircNativeAd = () => {
  return (
    <NativeAd
      targetingPos="native-article-related"
      targetingNative="native"
      lazyLoadScreens={3}
      className="s-native s-native--short-title s-native--standard s-native--small s-native--streamline"
    />
  );
};

const ArticleRelatedContentList = ({
  displayName,
  articles,
  count,
}: {
  displayName: string;
  articles: RelatedContentRiverFragment;
  count: number;
}) => {
  const edges =
    articles.edges.length > 3 ? articles.edges.slice(0, 3) : articles.edges;

  const [isMobile] = useMedia("(max-width: 576px)");
  const [abVersionActive, setAbVersionActive] = useState<boolean>(false);
  const [abVersionWithoutAuthor, setAbVersionWithoutAuthor] = useState<boolean>(
    false
  );
  const testGroup = useActiveTestGroup("Text-Only-Recommended-Reading");

  useEffect(() => {
    setAbVersionActive(
      isMobile &&
        (testGroup === "Text-Only-Recommended-Reading.authors" ||
          testGroup === "Text-Only-Recommended-Reading.no-authors")
    );
    setAbVersionWithoutAuthor(
      isMobile && testGroup === "Text-Only-Recommended-Reading.no-authors"
    );
  }, [isMobile, testGroup]);

  return (
    <section className={cx(abVersionActive ? styles.abRoot : styles.root)}>
      <h2 className={cx(styles.heading, abVersionActive && styles.abHeading)}>
        {displayName}
      </h2>
      <ul className={cx(abVersionActive ? styles.abList : styles.list)}>
        {edges.slice(0, 3).map((article, i) => {
          return (
            <li
              className={cx(
                styles.listItem,
                abVersionActive && styles.abListItem
              )}
              key={`article-recirc-rr-${i}`}
            >
              {/* Insert native ad before last article so the last article can be hidden with .ad-loaded + .content if an ad loads */}
              {!abVersionActive && i === edges.length - 1 && <RecircNativeAd />}
              <div
                className={cx(
                  abVersionActive ? styles.abContent : styles.content
                )}
                data-view-action={`view link - ${displayName.toLowerCase()} ${count} - item ${
                  i + 1
                }`}
                data-view-label={article.node.url}
              >
                {article.node.riverImage && !abVersionActive && (
                  <figure className={styles.figure}>
                    <a
                      className={styles.link}
                      href={article.node.url}
                      title={`Read More: ${article.node.title}`}
                      data-action={`click link - ${displayName.toLowerCase()} - image ${
                        i + 1
                      }`}
                      data-label={article.node.url}
                    >
                      <picture className={styles.picture}>
                        <Image
                          lazy={true}
                          width={article.node.riverImage.width}
                          height={article.node.riverImage.height}
                          className={styles.image}
                          alt={article.node.riverImage.altText || ""}
                          src={article.node.riverImage.url}
                          srcSet={article.node.riverImage.srcSet}
                        />
                      </picture>
                    </a>
                  </figure>
                )}
                <div
                  className={cx(
                    abVersionActive ? styles.abTextWrapper : styles.textWrapper
                  )}
                >
                  {article.node.title && (
                    <h3
                      className={cx(
                        abVersionActive ? styles.abTitle : styles.title
                      )}
                    >
                      <a
                        className={styles.link}
                        href={article.node.url}
                        dangerouslySetInnerHTML={{
                          __html: article.node.title,
                        }}
                        data-action={`click link - ${displayName.toLowerCase()} - title ${
                          i + 1
                        }`}
                        data-label={article.node.url}
                      ></a>
                    </h3>
                  )}
                  {article.node.authors && !abVersionWithoutAuthor && (
                    <address className={styles.byline}>
                      <Authors
                        authors={article.node.authors}
                        component={AuthorWrapper}
                        articleIndex={i}
                        heading={displayName}
                      />
                    </address>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

ArticleRelatedContentList.fragment = gql`
  fragment relatedContentAuthor on Author {
    url
    displayName
    id
    slug
  }

  fragment relatedContentRiver on RiverConnection {
    edges {
      node {
        url
        title
        authors {
          ...relatedContentAuthor
        }
        riverImage(width: 90, height: 90, sizes: ["90w", "180w"]) {
          id
          url
          srcSet
          width
          height
          altText
        }
      }
    }
  }
`;

export default ArticleRelatedContentList;
